import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../notification/notification.service';
import { PurchasingService, Supplier, SupplierItem } from '../../api';

interface SupplierForm {
  name: string;
  email: string;
  website: string;
  order_method: 'email' | 'webshop';
  payment_method: string;
  prices_gross: boolean;
  contact_name: string;
  language: string;
  min_order_huf: number | null;
  shipping_fee_huf: number | null;
  lead_time_days: number | null;
  note: string;
}

interface ItemForm {
  id: number | null;
  name: string;
  supplier_sku: string;
  url: string;
  unit_price_huf: number | null;
  pack_size: number | null;
  usual_qty: number | null;
  note: string;
}

const emptyItem = (): ItemForm => ({ id: null, name: '', supplier_sku: '', url: '', unit_price_huf: null, pack_size: null, usual_qty: null, note: '' });

/** Beszerzés → Beszállítók: kitől rendelünk (e-mail / webshop) és mit (link, cikkszám, kiszerelés, szokásos mennyiség). */
@Component({
  selector: 'app-beszallitok',
  templateUrl: './beszallitok.component.html',
  styleUrl: './beszallitok.component.scss',
})
export class BeszallitokComponent implements OnInit {
  loading = true;
  suppliers: Supplier[] = [];
  selectedId: number | null = null;
  form: SupplierForm | null = null;
  saving = false;
  itemForm: ItemForm | null = null;
  itemSaving = false;
  checking: Record<number, boolean> = {};

  constructor(private purchasing: PurchasingService, private notification: NotificationService) {}

  ngOnInit(): void {
    this.load();
  }

  load(selectId?: number): void {
    this.purchasing.listSuppliers().subscribe({
      next: (list) => {
        this.suppliers = list;
        this.loading = false;
        const id = selectId ?? this.selectedId ?? list[0]?.id ?? null;
        if (id && list.some((s) => s.id === id)) this.select(id, !!selectId || !this.form);
      },
      error: () => (this.loading = false),
    });
  }

  get selected(): Supplier | null {
    return this.suppliers.find((s) => s.id === this.selectedId) || null;
  }

  select(id: number, resetForm = true): void {
    this.selectedId = id;
    const s = this.selected;
    if (s && resetForm) {
      this.form = {
        name: s.name,
        email: s.email || '',
        website: s.website || '',
        order_method: s.order_method,
        payment_method: s.payment_method || 'utánvét',
        prices_gross: !!s.prices_gross,
        contact_name: s.contact_name || '',
        language: s.language || 'hu',
        min_order_huf: s.min_order_huf,
        shipping_fee_huf: s.shipping_fee_huf,
        lead_time_days: s.lead_time_days,
        note: s.note || '',
      };
      this.itemForm = null;
    }
  }

  newSupplier(): void {
    this.selectedId = null;
    this.itemForm = null;
    this.form = { name: '', email: '', website: '', order_method: 'webshop', payment_method: 'utánvét', prices_gross: true, contact_name: '', language: 'hu', min_order_huf: null, shipping_fee_huf: null, lead_time_days: null, note: '' };
  }

  saveSupplier(): void {
    if (!this.form) return;
    if (!this.form.name.trim()) {
      this.notification.show('Add meg a beszállító nevét.', 'warning');
      return;
    }
    this.saving = true;
    this.purchasing.saveSupplier(this.selectedId, this.form as any).subscribe({
      next: (saved) => {
        this.saving = false;
        this.notification.show('Beszállító mentve.', 'success');
        this.load(saved.id);
      },
      error: (e) => {
        this.saving = false;
        this.notification.show(e?.error?.message || 'Nem sikerült menteni.', 'danger');
      },
    });
  }

  /** Törlés: ha még nincs rendelése, végleg; ha van, archiválódik (a rendelések nyoma megmarad). */
  removeSupplier(): void {
    const s = this.selected;
    if (!s || !confirm(`Törlöd: ${s.name}? Ha már van rendelése, csak archiválódik.`)) return;
    this.purchasing.deleteSupplier(s.id).subscribe({
      next: (r) => {
        this.notification.show(r.deleted ? `${s.name} törölve.` : `${s.name} archiválva (van rendelése).`, 'success');
        this.selectedId = null;
        this.form = null;
        this.load();
      },
      error: (e) => this.notification.show(e?.error?.message || 'Nem sikerült.', 'danger'),
    });
  }

  archive(): void {
    const s = this.selected;
    if (!s || !confirm(`Archiválod: ${s.name}? A rendelési listán nem fog szerepelni.`)) return;
    this.purchasing.archiveSupplier(s.id, true).subscribe(() => {
      this.selectedId = null;
      this.form = null;
      this.load();
    });
  }

  // ------------------------------------------------------------ termékek

  editItem(item?: SupplierItem): void {
    this.itemForm = item
      ? {
          id: item.id,
          name: item.name,
          supplier_sku: item.supplier_sku || '',
          url: item.url || '',
          unit_price_huf: item.unit_price_huf !== null ? Number(item.unit_price_huf) : null,
          pack_size: item.pack_size,
          usual_qty: item.usual_qty,
          note: item.note || '',
        }
      : emptyItem();
  }

  saveItem(): void {
    const f = this.itemForm;
    const s = this.selected;
    if (!f || !s) return;
    if (!f.name.trim()) {
      this.notification.show('Add meg a termék nevét.', 'warning');
      return;
    }
    this.itemSaving = true;
    const { id, ...dto } = f;
    this.purchasing.saveItem(s.id, id, dto as any).subscribe({
      next: () => {
        this.itemSaving = false;
        this.itemForm = null;
        this.load(s.id);
      },
      error: (e) => {
        this.itemSaving = false;
        this.notification.show(e?.error?.message || 'Nem sikerült menteni.', 'danger');
      },
    });
  }

  toggleActive(item: SupplierItem): void {
    this.purchasing.saveItem(item.supplier_id, item.id, { active: !item.active }).subscribe(() => this.load(item.supplier_id));
  }

  deleteItem(item: SupplierItem): void {
    if (!confirm(`Törlöd: ${item.name}?`)) return;
    this.purchasing.deleteItem(item.supplier_id, item.id).subscribe(() => this.load(item.supplier_id));
  }

  checkPrice(item: SupplierItem): void {
    this.checking[item.id] = true;
    this.purchasing.checkPrice(item.id).subscribe({
      next: ({ web }) => {
        this.checking[item.id] = false;
        const stock = web.inStock === null ? '' : web.inStock ? ', raktáron' : ', NINCS raktáron';
        this.notification.show(web.price !== null ? `${web.name || item.name}: ${web.price} ${web.currency || ''} (nettó)${stock}` : 'Az oldalon nem találtam árat.', web.price !== null ? 'success' : 'warning');
        this.load(item.supplier_id);
      },
      error: (e) => {
        this.checking[item.id] = false;
        this.notification.show(e?.error?.message || 'Az ár nem kérhető le.', 'danger');
      },
    });
  }

  price(item: SupplierItem): number | null {
    return item.unit_price_huf !== null && item.unit_price_huf !== undefined ? Number(item.unit_price_huf) : null;
  }
}
